import { sql } from "drizzle-orm";
import {
  boolean,
  doublePrecision,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";

/**
 * Where one price range of a scrape run stands. A range is `pending` until a worker reads it,
 * then `done`, `failed`, or `split` when it held more than one Algolia query can return and its
 * two halves were queued in its place.
 */
export enum FanaticsScrapeChunkStatus {
  Pending = "pending",
  Done = "done",
  Split = "split",
  Failed = "failed",
}

export const fanaticsScrapeChunkStatusEnum = pgEnum("fanatics_scrape_chunk_status", [
  FanaticsScrapeChunkStatus.Pending,
  FanaticsScrapeChunkStatus.Done,
  FanaticsScrapeChunkStatus.Split,
  FanaticsScrapeChunkStatus.Failed,
]);

/**
 * One run of the complete Fanatics scrape (v2, `docs/fulfillment/fanatics-scrape.md`). Writer:
 * the `ScrapeFanaticsV2` cron, which opens a run and sweeps the previous one. Platform-internal:
 * no tenant reads it.
 */
export const fanaticsScrapeRuns = pgTable(
  "fanatics_scrape_runs",
  {
    id: text("id").primaryKey(),
    startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
    /** Set once the run's sweep has deactivated what it did not see, or it was abandoned. */
    sweptAt: timestamp("swept_at", { withTimezone: true }),
    /** Listings the sweep deactivated; null until swept, and on an abandoned run. */
    deactivated: integer("deactivated"),
    /** True when chunks were still pending past the abandon age: the run swept nothing. */
    abandoned: boolean("abandoned").notNull().default(false),
  },
  (t) => [index("fanatics_scrape_runs_swept_started_idx").on(t.sweptAt, t.startedAt)],
);

/**
 * One slice of one scrape target in a run: a price range, narrowed by guide-price ratio and year
 * when a price range a cent wide still holds too much (`fanatics-scrape/scrape-slice.ts`). Writer:
 * the cron (a target's whole range) and the scrape worker (its result, or the two halves it splits
 * into). `seenIds` holds every listing the
 * range returned, so the run's sweep can tell what Fanatics no longer lists without a per-listing
 * write. Platform-internal: no tenant reads it.
 */
export const fanaticsScrapeChunks = pgTable(
  "fanatics_scrape_chunks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    runId: text("run_id")
      .notNull()
      .references(() => fanaticsScrapeRuns.id, { onDelete: "cascade" }),
    /** A `FanaticsScrapeTarget` name (`fulfillment-functions/src/fanatics/scrape-targets.ts`). */
    target: text("target").notNull(),
    /** The target's listing category, which is what the sweep is scoped by. */
    category: text("category").notNull(),
    /** Fanatics `currentPrice` bounds in dollars: `>= priceMin`, `< priceMax`, open when null. */
    priceMin: doublePrecision("price_min").notNull(),
    priceMax: doublePrecision("price_max"),
    /**
     * `guidePriceRatio` bounds, set only on a slice of a one-cent price range that held more than
     * one query returns: `>= ratioMin` (`> 0` when it is 0), `< ratioMax`, open when null. Null
     * both means any ratio above 0.
     */
    ratioMin: doublePrecision("ratio_min"),
    ratioMax: doublePrecision("ratio_max"),
    /** `year` bounds, set only once the ratio is down to a cent too. Null both means any year. */
    yearMin: integer("year_min"),
    yearMax: integer("year_max"),
    status: fanaticsScrapeChunkStatusEnum("status")
      .notNull()
      .default(FanaticsScrapeChunkStatus.Pending),
    seenIds: text("seen_ids").array().notNull().default([]),
    hits: integer("hits").notNull().default(0),
    written: integer("written").notNull().default(0),
    stockChecked: integer("stock_checked").notNull().default(0),
    /** Times the redrive queued this chunk again after its message went quiet. */
    requeues: integer("requeues").notNull().default(0),
    error: text("error"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("fanatics_scrape_chunks_run_status_idx").on(t.runId, t.status),
    // The redrive's claim of stale pending chunks, every five minutes; pending rows are few.
    index("fanatics_scrape_chunks_pending_updated_idx")
      .on(t.updatedAt)
      .where(sql`${t.status} = 'pending'`),
  ],
);
